/* 
🎯 Shell UI Design System v3.0 - Status Indicator Component
Purpose: Clear status communication with morphing dot animations
Philosophy: Functional clarity, playful micro-interactions, honest feedback
*/

import React from 'react';

export const StatusIndicator = ({ 
  status = 'offline',
  text,
  showText = false,
  size = 'default',
  animated = true,
  className = '',
  ...props 
}) => {
  const getStatusConfig = () => {
    switch (status.toLowerCase()) {
      case 'online':
      case 'active':
      case 'ready':
      case 'connected':
        return {
          variant: 'online',
          defaultText: 'ONLINE',
          color: 'success'
        };
      case 'offline':
      case 'inactive':
      case 'disconnected':
        return {
          variant: 'offline',
          defaultText: 'OFFLINE',
          color: 'secondary'
        };
      case 'error':
      case 'failed':
      case 'critical':
        return {
          variant: 'error',
          defaultText: 'ERROR',
          color: 'error'
        };
      case 'warning':
      case 'degraded':
        return {
          variant: 'warning',
          defaultText: 'WARNING',
          color: 'warning'
        };
      case 'loading':
      case 'pending':
      case 'connecting':
        return {
          variant: 'loading',
          defaultText: 'LOADING',
          color: 'loading'
        };
      case 'idle':
      case 'standby':
        return {
          variant: 'idle',
          defaultText: 'IDLE',
          color: 'idle'
        };
      default:
        return {
          variant: 'unknown',
          defaultText: 'UNKNOWN',
          color: 'secondary'
        };
    }
  };

  const config = getStatusConfig();
  const displayText = text || config.defaultText;

  const baseClass = 'shell-status';
  const variantClass = `${baseClass}--${config.variant}`;
  const sizeClass = size !== 'default' ? `${baseClass}--${size}` : '';
  const animatedClass = animated ? `${baseClass}--animated` : '';
  
  const combinedClassName = [
    baseClass, 
    variantClass, 
    sizeClass, 
    animatedClass, 
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={combinedClassName} {...props}>
      <StatusDot 
        variant={config.variant} 
        animated={animated && config.variant === 'online'} 
      />
      {showText && (
        <span className="shell-status__text">
          {displayText}
        </span>
      )}
    </div>
  );
};

export const StatusDot = ({ 
  variant = 'offline',
  animated = false,
  className = ''
}) => {
  const baseClass = 'shell-status-dot';
  const variantClass = `${baseClass}--${variant}`;
  const animatedClass = animated ? `${baseClass}--animated` : '';
  
  const combinedClassName = [baseClass, variantClass, animatedClass, className]
    .filter(Boolean)
    .join(' ');

  return <div className={combinedClassName} />;
};

export const OnlineIndicator = ({ showText = true, ...props }) => (
  <StatusIndicator 
    status="online" 
    showText={showText}
    animated={true}
    {...props} 
  />
);

export const OfflineIndicator = ({ showText = true, ...props }) => (
  <StatusIndicator 
    status="offline" 
    showText={showText}
    animated={false}
    {...props} 
  />
);

export const ErrorIndicator = ({ showText = true, ...props }) => (
  <StatusIndicator 
    status="error" 
    showText={showText}
    animated={false}
    {...props} 
  />
);

export const LoadingIndicator = ({ showText = true, ...props }) => (
  <StatusIndicator 
    status="loading" 
    showText={showText}
    animated={true}
    {...props} 
  />
);

export const IdleIndicator = ({ showText = true, ...props }) => (
  <StatusIndicator 
    status="idle" 
    showText={showText}
    animated={false}
    {...props} 
  />
);

export const StatusGroup = ({ 
  children,
  orientation = 'horizontal',
  className = '',
  ...props 
}) => {
  const baseClass = 'shell-status-group';
  const orientationClass = `${baseClass}--${orientation}`;
  
  const combinedClassName = [baseClass, orientationClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={combinedClassName} {...props}>
      {children}
    </div>
  );
};

export const StatusBadge = ({ 
  status,
  count,
  className = '',
  ...props 
}) => {
  const config = {
    online: { color: 'success', text: 'Online' },
    offline: { color: 'secondary', text: 'Offline' },
    error: { color: 'error', text: 'Error' },
    warning: { color: 'warning', text: 'Warning' }
  }[status] || { color: 'secondary', text: 'Unknown' };

  const baseClass = 'shell-status-badge';
  const colorClass = `${baseClass}--${config.color}`;
  
  const combinedClassName = [baseClass, colorClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={combinedClassName} {...props}>
      <StatusDot 
        variant={status} 
        animated={status === 'online'} 
      />
      <span className="shell-status-badge__text">
        {config.text}
      </span>
      {count !== undefined && (
        <span className="shell-status-badge__count">
          {count}
        </span>
      )}
    </div>
  );
};

export default StatusIndicator;

/*
USAGE EXAMPLES:

// Basic status indicators
<StatusIndicator status="online" showText={true} />
<StatusIndicator status="error" showText={true} />
<StatusIndicator status="loading" showText={true} />

// Dot only (compact)
<StatusIndicator status="online" showText={false} />

// Using shorthand components
<OnlineIndicator />
<OfflineIndicator />
<ErrorIndicator />
<LoadingIndicator />
<IdleIndicator />

// Custom text
<StatusIndicator 
  status="online" 
  text="All Systems Go" 
  showText={true} 
/>

// Status group for multiple indicators
<StatusGroup>
  <StatusIndicator status="online" text="Server" showText={true} />
  <StatusIndicator status="online" text="Database" showText={true} />
  <StatusIndicator status="error" text="Cache" showText={true} />
</StatusGroup>

// Vertical status group
<StatusGroup orientation="vertical">
  <OnlineIndicator />
  <OfflineIndicator />
  <ErrorIndicator />
</StatusGroup>

// Status badges with counts
<StatusBadge status="online" count={12} />
<StatusBadge status="offline" count={3} />
<StatusBadge status="error" count={1} />

// Dynamic status based on data
const ShellStatus = ({ shell }) => {
  const getStatus = () => {
    if (shell.isRunning && shell.healthCheck) return 'online';
    if (shell.hasErrors) return 'error';
    if (!shell.isRunning) return 'offline';
    return 'loading';
  };
  
  return (
    <StatusIndicator 
      status={getStatus()} 
      text={shell.name}
      showText={true}
    />
  );
};

// Server dashboard with multiple status indicators
const ServerDashboard = ({ servers }) => (
  <StatusGroup>
    {servers.map(server => (
      <StatusBadge 
        key={server.id}
        status={server.status}
        count={server.activeConnections}
      />
    ))}
  </StatusGroup>
);

FEATURES:
- Multiple status variants (online, offline, error, warning, loading, idle)
- Morphing dot animation for online status
- Text labels with uppercase styling
- Compact dot-only mode
- Status groups for multiple indicators
- Status badges with counts
- Size variants
- Customizable text
- Animation control

PHILOSOPHY ALIGNMENT:
- Functional clarity - immediate status understanding
- Honest feedback - clear communication of system state
- Playful micro-interactions - morphing animation
- Oversized & tactile - easily readable status
- Monochrome + accent - selective use of color for meaning

ACCESSIBILITY:
- High contrast colors
- Text alternatives for status
- ARIA attributes for screen readers
- Keyboard navigation support
- Clear visual hierarchy

ANIMATION DETAILS:
- Online status gets morphing animation (square → circle → diamond)
- Error/offline states are static for stability
- Loading states can have subtle pulse effects
- Respects prefers-reduced-motion preferences
*/